import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  FileText, 
  Download, 
  TrendingUp,
  Scale,
  Wrench,
  Search,
  Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'market_insights', name: 'Market Insights', icon: TrendingUp },
  { id: 'legal', name: 'Legal & Compliance', icon: Scale },
  { id: 'tools', name: 'Tools & Templates', icon: Wrench },
  { id: 'guides', name: 'Downloadable Guides', icon: Download },
  { id: 'news', name: 'Industry News', icon: FileText },
];

const sampleResources = [
  {
    resource_id: 'res_sample1',
    title: '2024 Real Estate Market Outlook',
    description: 'Comprehensive analysis of market trends, forecasts, and opportunities for the coming year.',
    category: 'market_insights',
    is_public: true,
  },
  {
    resource_id: 'res_sample2',
    title: 'Buyer Agency Agreement Templates',
    description: 'Updated templates compliant with NAR settlement requirements.',
    category: 'tools',
    is_public: false,
  },
  {
    resource_id: 'res_sample3',
    title: 'Fair Housing Compliance Guide',
    description: 'Essential guidelines for maintaining fair housing practices in your business.',
    category: 'legal',
    is_public: true,
  },
  {
    resource_id: 'res_sample4',
    title: 'Lead Generation Strategies',
    description: 'Proven techniques for generating and converting real estate leads.',
    category: 'guides',
    is_public: false,
  },
  {
    resource_id: 'res_sample5',
    title: 'Interest Rate Impact Analysis',
    description: 'How recent rate changes affect buyer behavior and market dynamics.',
    category: 'market_insights',
    is_public: true,
  },
  {
    resource_id: 'res_sample6',
    title: 'Contract Negotiation Checklist',
    description: 'Step-by-step checklist for successful contract negotiations.',
    category: 'tools',
    is_public: false,
  },
];

export default function Resources() {
  const { isActiveMember } = useAuth();
  const [resources, setResources] = useState(sampleResources);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchResources = async () => {
      setLoading(true);
      try {
        const url = selectedCategory === 'all' 
          ? `${API}/resources` 
          : `${API}/resources?category=${selectedCategory}`;
        
        const response = await fetch(url, { credentials: 'include' });
        
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setResources(data);
          }
        }
      } catch (error) {
        console.error('Error fetching resources:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, [selectedCategory]);

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.icon : FileText;
  };

  return (
    <div className="bg-white" data-testid="resources-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <span className="text-[#D42B3C] font-semibold text-sm tracking-wider uppercase mb-4 block">
              Resources
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Member Resources
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Access exclusive market insights, tools, templates, and educational materials 
              to elevate your real estate business.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input 
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg border-slate-200 focus:border-[#3D4F7A] focus:ring-[#3D4F7A]"
                data-testid="resource-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Tabs defaultValue="all" onValueChange={setSelectedCategory}>
            <TabsList className="flex flex-wrap justify-center gap-2 mb-12 bg-transparent">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-[#3D4F7A] data-[state=active]:text-white px-6 py-2 rounded-full"
              >
                All Resources
              </TabsTrigger>
              {categories.map(category => (
                <TabsTrigger 
                  key={category.id}
                  value={category.id}
                  className="data-[state=active]:bg-[#3D4F7A] data-[state=active]:text-white px-6 py-2 rounded-full"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={selectedCategory}>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
                </div>
              ) : filteredResources.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No resources found matching your criteria.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredResources.map((resource) => {
                    const Icon = getCategoryIcon(resource.category);
                    const isLocked = !resource.is_public && !isActiveMember;
                    
                    return (
                      <Card 
                        key={resource.resource_id} 
                        className={`border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 ${
                          isLocked ? 'opacity-75' : ''
                        }`}
                      >
                        <CardHeader className="pb-4">
                          <div className="flex items-start justify-between">
                            <div className="w-12 h-12 rounded-xl bg-[#3D4F7A]/10 flex items-center justify-center">
                              <Icon className="w-6 h-6 text-[#3D4F7A]" />
                            </div>
                            {isLocked && (
                              <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                                <Lock className="w-3 h-3" />
                                Members Only
                              </div>
                            )}
                          </div>
                          <CardTitle className="text-lg font-heading mt-4">{resource.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-slate-600 text-sm mb-4">{resource.description}</p>
                          {isLocked ? (
                            <Link to="/register">
                              <Button variant="outline" className="w-full border-[#3D4F7A] text-[#3D4F7A]">
                                Unlock with Membership
                              </Button>
                            </Link>
                          ) : (
                            <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
                              View Resource
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA for Non-Members */}
      {!isActiveMember && (
        <section className="py-20 md:py-28 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
            <Lock className="w-16 h-16 text-[#3D4F7A] mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Unlock All Resources
            </h2>
            <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
              Join AREA to access our complete library of market insights, tools, templates, and educational content.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-[#3D4F7A] hover:bg-[#2A3755] text-white px-10 py-6 text-lg font-semibold">
                Join AREA
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
